import { QueryKeys } from '@common/queries/common';
import { useReplayModeType } from '@common/Replay/utils';
import { Private_Routes, Public_Routes } from '@common/url';
import { UserTypes } from '@common/UserTypes';
import { theme } from '@prophecy/theme';
import { Stack, StackItem } from '@prophecy/ui-v3';
import { tokens as alertTokens } from '@prophecy/ui-v3/LegacyAlert/tokens';
import { Alert } from '@prophecy/ui-v3/ui-compat';
import { getDuration } from '@prophecy/utils/date';
import { useTimedFlag } from '@prophecy/utils/react/hooks';
import { pluralize } from '@prophecy/utils/string';
import { noop, uniq, uniqBy } from 'lodash-es';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { useAppMetadata } from '../../context/appMetadata';
import { getMonitoringAlertInfo } from '../../data/apis/api';
import { useRestQuery } from '../../data/util';
import { MetricType, MonitoringAlert, ServiceDetails } from './types';

const StyledAlert = styled(Alert)`
  a {
    text-decoration: underline;
    ${(props) => props.variant === 'error' && `color: ${alertTokens.Alert.error.color};`}
    ${(props) => props.variant === 'warning' && `color: ${alertTokens.Alert.warning.color};`}
    text-underline-offset: ${theme.spaces.x2};
    font-size: ${alertTokens.Alert.fontSize};
  }
`;

function toMetricName(metricType: MetricType) {
  switch (metricType) {
    case 'CPU_USAGE':
      return 'CPU';
    case 'MEMORY_USAGE':
      return 'Memory';
    case 'DISK_USAGE':
      return 'Disk';
    case 'FILE_COUNT':
      return 'File Storage';
  }
}

export function MonitoringNotification() {
  const isAdminUser = useAppMetadata()?.user?.type === UserTypes.ClusterAdmin;
  const isLoggedIn = Boolean(useAppMetadata()?.user?.id);
  const { hasReplayShareMode } = useReplayModeType();
  const searchParams = new URLSearchParams(window.location.search);
  const isOnboardingTour = searchParams.get('onboardingTour') === 'true';
  const [showNotice, setShowNotice] = useState<boolean>(true);
  // refetch every 5 minutes
  const { data } = useRestQuery<MonitoringAlert>([QueryKeys.MonitoringAlert], getMonitoringAlertInfo, {
    refetchInterval: 5 * 60 * 1000,
    // if the alert API itself fails then do not show error
    onError: noop
  });

  const criticalServices = useMemo(() => data?.data.critical || [], [data]);
  // show warning only for disk usage
  const warningServices = useMemo(
    () =>
      data?.data.warning?.filter(
        (service) => service.metricsType === 'DISK_USAGE' || service.metricsType === 'FILE_COUNT'
      ) || [],
    [data]
  );
  const getServiceNames = (details: ServiceDetails[]) => {
    const serviceNames = details.map((service) =>
      service.serviceName.startsWith('sandbox:') ? 'sandbox' : service.serviceName
    );
    const resourceNames = details.map((service) => toMetricName(service.metricsType));

    return {
      serviceNames: uniq(serviceNames).sort().join(', '),
      resourceNames
    };
  };
  const location = useLocation();
  const isMonitoringPage =
    location.pathname.includes(Private_Routes.Settings.tab.getUrl({ tab: 'admin' })) &&
    location.search.includes('tab=monitoring');
  const isProphecyDownPage = location.pathname.includes(Public_Routes.ProphecyDown.getUrl());
  const { serviceNames: criticalServiceNames, resourceNames: criticalResourceNames } =
    getServiceNames(criticalServices);
  const { serviceNames: warningServiceNames, resourceNames: warningResourceNames } = getServiceNames(warningServices);
  const resourceNames = uniq(criticalResourceNames.length > 0 ? criticalResourceNames : warningResourceNames)
    .sort()
    .join(', ');
  // get unique suspending service names
  const suspendingServices = uniqBy(
    criticalServices.filter((service) => Boolean(service.suspensionCountDownInSeconds)),
    'serviceName'
  ).map((service) => ({
    serviceName: service.serviceName,
    suspensionCountDown: getDuration(service.suspensionCountDownInSeconds as number, false)
  }));

  const hasAlerts = criticalServices.length > 0 || warningServices.length > 0;

  const _1hour = 60 * 60 * 1000;
  const [snoozeNotice, toggleSnooze] = useTimedFlag(_1hour);

  useEffect(() => {
    if ((criticalServices.length > 0 || warningServices.length > 0) && !snoozeNotice) {
      setShowNotice(true);
    }
  }, [criticalServices, warningServices, snoozeNotice]);

  const renderMessage = (serviceNames: string) => {
    const serviceSuspensionMessage =
      suspendingServices.length > 0 ? (
        <>
          ,{' '}
          {suspendingServices.map((service, index) => (
            <React.Fragment key={service.serviceName}>
              {index > 0 && ' and '}
              <strong>{service.serviceName}</strong> will be suspended in <strong>{service.suspensionCountDown}</strong>
            </React.Fragment>
          ))}{' '}
          to prevent data corruption
        </>
      ) : null;

    return (
      <>
        <strong>{resourceNames}</strong> utilization of {pluralize(serviceNames.length, 'service')}{' '}
        <strong>{serviceNames}</strong> {criticalServiceNames.length > 0 ? 'have reached' : 'will reach'} critical
        limits and requires your immediate attention{serviceSuspensionMessage}
        {isAdminUser ? '.' : ', please reach out to the cluster admin.'}
      </>
    );
  };

  const description: React.ReactNode = (
    <div>{hasAlerts ? renderMessage(criticalServiceNames) : renderMessage(warningServiceNames)}</div>
  );

  const showAlerts =
    isLoggedIn &&
    !isMonitoringPage &&
    !isProphecyDownPage &&
    showNotice &&
    hasAlerts &&
    !snoozeNotice &&
    !hasReplayShareMode &&
    !isOnboardingTour;
  // for admin we show all the notifications, for non-admin we show only when there are suspending services
  const hasSuspendingServices = suspendingServices.length > 0;
  const showAlertBanner = isAdminUser ? showAlerts : hasSuspendingServices && showAlerts;

  return showAlertBanner ? (
    <StyledAlert
      banner
      key='monitoringNotification'
      closable={true}
      variant={criticalServices.length > 0 ? 'error' : 'warning'}
      onClose={() => toggleSnooze()}>
      <Stack direction='horizontal' align='space-between' gap={theme.spaces.x4}>
        {description}
        {isAdminUser && (
          <StackItem shrink='0'>
            <Link target='_blank' to={Private_Routes.Settings.tab.getUrl({ tab: 'admin' }, { tab: 'monitoring' })}>
              More Details
            </Link>
          </StackItem>
        )}
      </Stack>
    </StyledAlert>
  ) : null;
}
